import { SupportedLanguages } from "../../../../libs/i18next/language";
import { Utilities } from "../../../../utils/utilities";
import CollectionLoader, { CollectionLoadedListener } from "../../General/CollectionLoader";
import { ReadAccess } from "../../General/AccessData";
import { FirebaseData } from "../../General/FirebaseData";
import { LocalData } from "../../General/LocalData";
import { TranslationData } from "../../General/TranslationData";
import { PropertyData } from "../../Property/PropertyData";
import { CollectionItemTemplateData } from "./CollectionItemTemplateData";
import { CompanyCollectionItemData } from "./CompanyCollectionItemData";
import { CompanyCustomFieldData } from "./CompanyCustomFieldData";

export type CollectionConnections = {
  deviations: boolean;
  routines: boolean;
  propertyFiles: boolean;
};

export class CompanyCollectionData extends FirebaseData {
  customFields: CollectionLoader<CompanyCustomFieldData>;
  itemTemplates: CollectionLoader<CollectionItemTemplateData>;
  items: CollectionLoader<CompanyCollectionItemData>;

  standardLangCode: SupportedLanguages = SupportedLanguages.EN;
  name: TranslationData = new TranslationData();
  allProperties: boolean = false;
  selectedProperties: PropertyData[] = [];
  previewFields: string[] = [];
  connections: CollectionConnections = { deviations: true, routines: true, propertyFiles: true };
  readAccess: ReadAccess = {};
  requiredWriteAccess: ReadAccess | undefined = undefined;
  itemTemplatesCount: number = 0;
  archived: boolean = false;
  customFieldsOrder: string[] = [];

  constructor(id: string, collectionName: string, parent: FirebaseData | undefined) {
    super(id, collectionName, parent);
    this.customFields = new CollectionLoader("customFields", this, CompanyCustomFieldData);
    this.itemTemplates = new CollectionLoader("itemTemplates", this, CollectionItemTemplateData);
    this.items = new CollectionLoader("items", this, CompanyCollectionItemData);
  }

  setup(data: any) {
    this.standardLangCode = SupportedLanguages.getLanguageWithCode(data.standardLangCode);
    this.name.setup(data.name, this.standardLangCode);
    this.archived = typeof data.archived === "boolean" ? data.archived : false;

    this.allProperties = typeof data.allProperties === "boolean" ? data.allProperties : false;
    this.selectedProperties = Array.isArray(data.selectedProperties)
      ? (data.selectedProperties as any[]).filter((el) => typeof el === "string").map((id) => LocalData.properties.addData(id))
      : [];
    this.previewFields = Array.isArray(data.previewFields) ? data.previewFields.filter((field: any) => typeof field === "string") : [];
    this.itemTemplatesCount = typeof data.itemTemplatesCount === "number" ? data.itemTemplatesCount : 0;
    this.customFieldsOrder = Array.isArray(data.customFieldsOrder) ? data.customFieldsOrder : [];

    this.connections = { deviations: true, routines: true, propertyFiles: true };
    if (typeof data.connections === "object") {
      this.connections = {
        deviations: typeof data.connections.deviations === "boolean" ? data.connections.deviations : true,
        routines: typeof data.connections.routines === "boolean" ? data.connections.routines : true,
        propertyFiles: typeof data.connections.propertyFiles === "boolean" ? data.connections.propertyFiles : true,
      };
    }

    this.readAccess = Utilities.getReadAccess(data.readAccess);
    this.requiredWriteAccess = Utilities.getOptionalReadAccess(data.requiredWriteAccess);

    super.setup(data);
  }

  setupCustomFieldsListener(onUpdate: () => void): CollectionLoadedListener<CompanyCustomFieldData>[] {
    return [this.customFields.setupRealtimeListener(onUpdate)];
  }
  loadCustomFields(onUpdate: (fields: CompanyCustomFieldData[]) => void) {
    return this.customFields.load(onUpdate);
  }

  loadAllItems(): Promise<void> {
    return this.items.loadWithPromise().then(() => {});
  }
}

